import { createSlice, EntityId, PayloadAction } from '@reduxjs/toolkit';
import { CouponInDTO } from 'app/main/design-system/models/coupon.type';
import { ClientEntity } from 'app/main/pages/clients/type';
import { HereAutoSuggest } from 'app/services/hereServices/types';
import {
  CampaignCouponClaimPage,
  CampaignFiltersDTO,
  CommunicationCampaignDetailInDTO,
  CommunicationCampaignOutDTO,
  CommunicationCampaignRevenueInDTO,
  CrmFieldFilter,
  CurrentCampaign,
  FilterType,
  NotificationType,
} from '../campaign.type';
import { CampaignTemplateItemDTO } from '../template.type';
import { normaliseFilterTypes } from '../utils/filterTypes';
import { notificationAdapter } from './notification.adapter';
import {
  getCampaignCouponClaimsThunk,
  getClientsForCampaignSearchThunk,
  getCommunicationCampaignCouponThunk,
  getCommunicationCampaignInfoThunk,
  getCommunicationCampaignStatsThunk,
  getCurrentCampaignThunk as getCurrentCommunicationCampaignThunk,
  getNotificationCampaignsThunk,
} from './notification.thunks';
import {
  createNewTemplateFromCampaignThunk,
  deleteCampaignTemplateThunk,
  getCampaignInfoFromTemplateThunk,
  getCampaignTemplateListThunk,
} from './template.thunks';

export interface NotificationCampaignState {
  ids: string[];
  entities: { [key: string]: CommunicationCampaignRevenueInDTO };
  current: CurrentCampaign;
  selected: {
    info: {
      entity?: CommunicationCampaignDetailInDTO;
      isLoading: boolean;
      hasError: boolean;
    };
    stats: {
      data?: CommunicationCampaignRevenueInDTO;
      isLoading: boolean;
      hasError: boolean;
    };
    coupon: {
      data?: CouponInDTO;
      isLoading: boolean;
      hasError: boolean;
    };
    couponClaims: {
      data?: CampaignCouponClaimPage;
      isLoading: boolean;
      hasError: boolean;
    };
  };
  areCampaignsLoading: boolean;
  hasCampaignsLoadingError: boolean;
  campaignValidationDialog: {
    isOpen: boolean;
    hasBeenSaved: boolean;
    isLoading: boolean;
  };
  createTemplateDialog: {
    isOpen: boolean;
    isLoading: boolean;
    hasError: boolean;
  };
  campaignTemplateDialog: {
    isOpen: boolean;
    isLoading: boolean;
    hasError?: boolean;
  };
  isFormCouponVisible: boolean;
  isFormPostcodeVisible: boolean;
  addressPostcode: HereAutoSuggest | null;
  currentFiltersTypes: FilterType[];
  hasErrorFilter: boolean;
  areAdvancedFiltersValid: boolean;
  notifiedUsers: {
    numberOfNotifiedUsers: number;
    isNotifiedUsersLoading: boolean;
  };
  userList: ClientEntity[];
  isUserListLoading: boolean;
  hasUserListError: boolean;
  warningDialogState: {
    isOpen: boolean;
    context: 'STORE' | 'TYPE';
    storeIds?: EntityId[];
    types?: FilterType[];
    clearedFamily?: FilterType;
  };
  templateList: CampaignTemplateItemDTO[];
}

export const initialState: NotificationCampaignState = {
  areCampaignsLoading: false,
  hasCampaignsLoadingError: false,
  ids: [],
  entities: {},
  selected: {
    info: {
      isLoading: true,
      hasError: false,
    },
    stats: {
      isLoading: true,
      hasError: false,
    },
    coupon: {
      isLoading: true,
      hasError: false,
    },
    couponClaims: {
      isLoading: false,
      hasError: false,
    },
  },
  current: {
    communicationCampaign: {
      name: '',
      userId: '',
      type: NotificationType.notification,
      averageBasket: undefined,
      notification: {
        title: '',
        message: '',
        imageUrl: undefined,
      },
      isScheduled: false,
      campaignFilters: {
        storeIds: [],
        clientIds: undefined,
        segmentRef: undefined,
        minAge: 0,
        maxAge: 100,
        gender: undefined,
        minLoyaltyPoints: undefined,
        maxLoyaltyPoints: undefined,
        minCollectedPoints: undefined,
        maxCollectedPoints: undefined,
        minAverageBasket: undefined,
        maxAverageBasket: undefined,
        minWeeklyAverageBasket: undefined,
        maxWeeklyAverageBasket: undefined,
        startPassageDate: undefined,
        endPassageDate: undefined,
        minNbPassageTransactions: undefined,
        maxNbPassageTransactions: undefined,
        zipOrPostcodes: [],
        radius: null,
        originProviderId: null,
        clientsAcquiredFrom: [],
        clientsTypes: [],
        crmFieldFilters: undefined,
        consumedProductIds: undefined,
        consumedProductsMatch: 'any',
        startConsumptionDate: undefined,
        endConsumptionDate: undefined,
        minConsumedQuantity: undefined,
        fromConnector: undefined,
      },
      dateCreated: '',
    },
    news: {
      title: '',
      message: '',
      imageUrl: undefined,
      startDate: '',
      endDate: '',
      dateCreated: '',
    },
    coupon: undefined,
    isNewsDifferentFromCampaign: false,
    hasCoupon: false,
    hasLink: false,
    isSMSLengthValid: false,
    merchantUserId: '',
    startTime: undefined,
    endTime: undefined,
    isSent: false,
    isTemplate: false,
  },
  campaignValidationDialog: {
    isOpen: false,
    hasBeenSaved: false,
    isLoading: false,
  },
  createTemplateDialog: {
    isOpen: false,
    isLoading: false,
    hasError: false,
  },
  campaignTemplateDialog: {
    isOpen: false,
    isLoading: false,
  },
  isFormCouponVisible: false,
  isFormPostcodeVisible: false,
  addressPostcode: null,
  currentFiltersTypes: [FilterType.NONE],
  hasErrorFilter: false,
  areAdvancedFiltersValid: true,
  notifiedUsers: {
    numberOfNotifiedUsers: 0,
    isNotifiedUsersLoading: false,
  },
  userList: [],
  isUserListLoading: false,
  hasUserListError: false,
  warningDialogState: {
    isOpen: false,
    context: 'STORE',
  },
  templateList: [],
};

export const { actions, reducer } = createSlice({
  name: 'notificationCampaign',
  initialState: notificationAdapter.getInitialState(initialState),
  reducers: {
    updateCampaign: (state, action) => {
      const { id, changes } = action.payload;
      notificationAdapter.updateOne(state, { id, changes });
    },
    deleteCampaign: (state, action) => {
      const { id } = action.payload;
      notificationAdapter.removeOne(state, id);
    },
    updateCurrentCampaignFilters: (state, action: PayloadAction<Partial<CampaignFiltersDTO>>) => {
      state.current.communicationCampaign.campaignFilters = {
        ...state.current.communicationCampaign.campaignFilters,
        ...action.payload,
      };
    },
    updateCurrentCamapignCrmFields: (state, action: PayloadAction<{ id: string; fieldFilter: CrmFieldFilter }>) => {
      const { crmFieldFilters: crmFormFields } = state.current.communicationCampaign.campaignFilters;
      if (!crmFormFields) {
        state.current.communicationCampaign.campaignFilters.crmFieldFilters = [action.payload.fieldFilter];
        return;
      }
      const index = crmFormFields?.findIndex(crmField => crmField.fieldId === action.payload.id);
      if (index >= 0) {
        crmFormFields.splice(index, 1, action.payload.fieldFilter);
      } else {
        crmFormFields.push(action.payload.fieldFilter);
      }
      state.current.communicationCampaign.campaignFilters.crmFieldFilters = crmFormFields;
    },
    deleteCurrentCampaignCrmField: (state, action: PayloadAction<string>) => {
      const { crmFieldFilters: crmFormFields } = state.current.communicationCampaign.campaignFilters;
      state.current.communicationCampaign.campaignFilters.crmFieldFilters = crmFormFields?.filter(
        crmField => crmField.fieldId !== action.payload,
      );
    },
    setCurrentCampaignFiltersTypes: (state, action: PayloadAction<FilterType[]>) => {
      state.currentFiltersTypes = normaliseFilterTypes(action.payload);
    },
    updateCurrentCampaignSMS: (state, action) => {
      state.current.sms = {
        ...state.current.sms,
        ...action.payload,
      };
      state.areCampaignsLoading = false;
      state.hasCampaignsLoadingError = false;
    },
    clearCurrentCampaignSMS: state => {
      state.current.sms = undefined;
    },
    updateCurrentCampaignNotificationData: (state, action) => {
      state.current.communicationCampaign.notification = {
        ...state.current.communicationCampaign.notification,
        ...action.payload,
      };
    },
    updateCurrentCampaign: (state, action: PayloadAction<Partial<CommunicationCampaignOutDTO>>) => {
      state.current.communicationCampaign = { ...state.current.communicationCampaign, ...action.payload };
    },
    updateCurrentNews: (state, action) => {
      state.current.news = { ...state.current.news, ...action.payload };
    },
    updateCurrentCoupon: (state, action) => {
      state.current.coupon = { ...state.current.coupon, ...action.payload };
    },
    updateCurrentLinkStatus: (state, action) => {
      state.current.hasLink = action.payload;
    },
    updateCurrentFullCampaign: (state: NotificationCampaignState, action: PayloadAction<Partial<CurrentCampaign>>) => {
      state.current = { ...state.current, ...action.payload };
    },
    updateisNewsDifferentFromCampaign: (state, action: PayloadAction<boolean>) => {
      state.current.isNewsDifferentFromCampaign = action.payload;
    },
    initTemplate: (state, action: PayloadAction<EntityId[]>) => {
      state.current.isTemplate = true;
      state.selected.info.isLoading = false;
      state.current.communicationCampaign.campaignFilters.storeIds = action.payload;
    },
    updateSelectCampaigninfo: (state, action) => {
      state.selected.info = action.payload;
    },
    getCampaignsFailed: state => {
      notificationAdapter.setAll(state, []);
      state.areCampaignsLoading = false;
      state.hasCampaignsLoadingError = true;
    },
    setCampaignsAreLoading: (state, action) => {
      state.areCampaignsLoading = action.payload;
    },
    resetCurrent: () => notificationAdapter.getInitialState(initialState),
    resetCouponClaims: state => {
      state.selected.couponClaims = initialState.selected.couponClaims;
    },
    resetFilters: state => {
      const { storeIds, ...resetFilters } = initialState.current.communicationCampaign.campaignFilters;
      const { storeIds: currentStoreIds } = state.current.communicationCampaign.campaignFilters;
      state.current.communicationCampaign.campaignFilters = {
        ...resetFilters,
        storeIds: currentStoreIds,
      };
      state.isFormPostcodeVisible = false;
      state.areAdvancedFiltersValid = true;
      state.userList = [];
    },
    resetProfileFilters: state => {
      const { storeIds, clientIds, segmentRef, ...profileDefaults } =
        initialState.current.communicationCampaign.campaignFilters;
      state.current.communicationCampaign.campaignFilters = {
        ...state.current.communicationCampaign.campaignFilters,
        ...profileDefaults,
      };
      state.isFormPostcodeVisible = false;
      state.areAdvancedFiltersValid = true;
    },
    resetCategoryFilter: state => {
      state.current.communicationCampaign.campaignFilters.segmentRef = undefined;
    },
    resetClientsFilter: state => {
      state.current.communicationCampaign.campaignFilters.clientIds = undefined;
      state.userList = [];
    },
    openCampaignValidationDialog: state => {
      state.campaignValidationDialog = {
        ...state.campaignValidationDialog,
        isOpen: true,
        isLoading: false,
      };
    },
    closeCampaignValidationDialog: state => {
      state.campaignValidationDialog = {
        ...state.campaignValidationDialog,
        isOpen: false,
        isLoading: false,
      };
    },
    setCampaignValidationDialog: (state, action) => {
      state.campaignValidationDialog = {
        ...state.campaignValidationDialog,
        ...action.payload,
      };
    },
    setFormCouponIsVisible: (state, action) => {
      state.isFormCouponVisible = action.payload;
    },
    setFormPostcodeIsVisible: (state, action) => {
      state.isFormPostcodeVisible = action.payload;
    },
    setAddressPostcode: (state, action) => {
      state.addressPostcode = action.payload;
    },
    setHasErrorFilter: (state, action) => {
      state.hasErrorFilter = action.payload;
    },
    setNotifiedUsers: (state, action: PayloadAction<number>) => {
      state.notifiedUsers.numberOfNotifiedUsers = action.payload;
    },
    setNotifiedUsersLoading: (state, action) => {
      state.notifiedUsers.isNotifiedUsersLoading = action.payload;
    },
    setUserList: (state, action: PayloadAction<ClientEntity[]>) => {
      state.userList = action.payload;
    },
    updateWarningDialogState: (state, action: PayloadAction<NotificationCampaignState['warningDialogState']>) => {
      state.warningDialogState = action.payload;
    },
    resetWarningDialogState: state => {
      state.warningDialogState = initialState.warningDialogState;
    },
    updateAdvancedFiltersValid: (state, action: PayloadAction<boolean>) => {
      state.areAdvancedFiltersValid = action.payload;
    },
    updateCampaignTemplateDialog: (
      state,
      action: PayloadAction<Partial<NotificationCampaignState['campaignTemplateDialog']>>,
    ) => {
      state.campaignTemplateDialog = {
        ...state.campaignTemplateDialog,
        ...action.payload,
      };
    },
    resetNotificationSlice: () => initialState,
  },
  extraReducers: builder => {
    builder.addCase(getCurrentCommunicationCampaignThunk.pending, state => {
      state.selected.info.isLoading = true;
      state.selected.info.hasError = false;
    });
    builder.addCase(getCurrentCommunicationCampaignThunk.fulfilled, (state, action) => {
      state.selected.info.isLoading = false;
      state.selected.info.hasError = false;
      state.current = { ...state.current, ...action.payload };
    });
    builder.addCase(getCurrentCommunicationCampaignThunk.rejected, state => {
      state.selected.info.isLoading = false;
      state.selected.info.hasError = true;
    });

    builder.addCase(getCommunicationCampaignInfoThunk.pending, state => {
      state.selected.info.isLoading = true;
      state.selected.info.hasError = false;
    });
    builder.addCase(getCommunicationCampaignInfoThunk.fulfilled, (state, action) => {
      state.selected.info.isLoading = false;
      state.selected.info.hasError = false;
      state.selected.info.entity = action.payload;
    });
    builder.addCase(getCommunicationCampaignInfoThunk.rejected, state => {
      state.selected.info.isLoading = false;
      state.selected.info.hasError = true;
    });

    builder.addCase(getCommunicationCampaignStatsThunk.pending, state => {
      state.selected.stats.isLoading = true;
      state.selected.stats.hasError = false;
    });
    builder.addCase(getCommunicationCampaignStatsThunk.fulfilled, (state, action) => {
      state.selected.stats.isLoading = false;
      state.selected.stats.hasError = false;
      state.selected.stats.data = action.payload;
    });
    builder.addCase(getCommunicationCampaignStatsThunk.rejected, state => {
      state.selected.stats.isLoading = false;
      state.selected.stats.hasError = true;
    });

    builder.addCase(getCampaignCouponClaimsThunk.pending, state => {
      state.selected.couponClaims.isLoading = true;
      state.selected.couponClaims.hasError = false;
    });
    builder.addCase(getCampaignCouponClaimsThunk.fulfilled, (state, action) => {
      state.selected.couponClaims.isLoading = false;
      state.selected.couponClaims.hasError = false;
      state.selected.couponClaims.data = action.payload;
    });
    builder.addCase(getCampaignCouponClaimsThunk.rejected, state => {
      state.selected.couponClaims.isLoading = false;
      state.selected.couponClaims.hasError = true;
    });

    builder.addCase(getCommunicationCampaignCouponThunk.pending, state => {
      state.selected.coupon.isLoading = true;
      state.selected.coupon.hasError = false;
    });
    builder.addCase(getCommunicationCampaignCouponThunk.fulfilled, (state, action) => {
      state.selected.coupon.isLoading = false;
      state.selected.coupon.hasError = false;
      state.selected.coupon.data = action.payload;
    });
    builder.addCase(getCommunicationCampaignCouponThunk.rejected, state => {
      state.selected.coupon.isLoading = false;
      state.selected.coupon.hasError = true;
    });
    builder.addCase(getNotificationCampaignsThunk.pending, state => {
      state.areCampaignsLoading = true;
      state.hasCampaignsLoadingError = false;
    });
    builder.addCase(getNotificationCampaignsThunk.fulfilled, (state, action) => {
      state.areCampaignsLoading = false;
      state.hasCampaignsLoadingError = false;
      notificationAdapter.setAll(state, action.payload);
    });
    builder.addCase(getNotificationCampaignsThunk.rejected, state => {
      state.areCampaignsLoading = false;
      state.hasCampaignsLoadingError = true;
      notificationAdapter.setAll(state, []);
    });
    builder.addCase(createNewTemplateFromCampaignThunk.pending, state => {
      state.campaignTemplateDialog.isLoading = true;
    });
    builder.addCase(createNewTemplateFromCampaignThunk.fulfilled, state => {
      state.campaignTemplateDialog.isLoading = false;
      state.campaignTemplateDialog.isOpen = false;
    });
    builder.addCase(createNewTemplateFromCampaignThunk.rejected, state => {
      state.campaignTemplateDialog.isLoading = false;
      state.campaignTemplateDialog.isOpen = false;
    });
    builder.addCase(getCampaignInfoFromTemplateThunk.pending, state => {
      state.selected.info.isLoading = true;
      state.selected.info.hasError = false;
    });
    builder.addCase(getCampaignTemplateListThunk.pending, state => {
      state.campaignTemplateDialog.isLoading = true;
      state.campaignTemplateDialog.hasError = false;
    });
    builder.addCase(getCampaignTemplateListThunk.fulfilled, (state, action) => {
      state.campaignTemplateDialog.isLoading = false;
      state.campaignTemplateDialog.hasError = false;
      state.templateList = action.payload;
    });
    builder.addCase(getCampaignTemplateListThunk.rejected, state => {
      state.campaignTemplateDialog.isLoading = false;
      state.campaignTemplateDialog.hasError = true;
      state.templateList = [];
    });
    builder.addCase(getCampaignInfoFromTemplateThunk.fulfilled, state => {
      state.selected.info.isLoading = false;
      state.selected.info.hasError = false;
    });
    builder.addCase(getCampaignInfoFromTemplateThunk.rejected, state => {
      state.selected.info.isLoading = false;
      state.selected.info.hasError = true;
    });
    builder.addCase(deleteCampaignTemplateThunk.pending, state => {
      state.createTemplateDialog.isLoading = true;
    });
    builder.addCase(deleteCampaignTemplateThunk.fulfilled, (state, action) => {
      state.createTemplateDialog.isLoading = false;
      state.createTemplateDialog.isOpen = false;
      state.templateList = state.templateList.filter(template => template.id !== action.payload);
    });
    builder.addCase(deleteCampaignTemplateThunk.rejected, state => {
      state.createTemplateDialog.isLoading = false;
      state.createTemplateDialog.isOpen = false;
    });

    builder.addCase(getClientsForCampaignSearchThunk.pending, state => {
      state.isUserListLoading = true;
      state.hasUserListError = false;
    });
    builder.addCase(getClientsForCampaignSearchThunk.fulfilled, (state, action) => {
      state.isUserListLoading = false;
      state.hasUserListError = false;
      state.userList = action.payload.data;
    });
    builder.addCase(getClientsForCampaignSearchThunk.rejected, state => {
      state.isUserListLoading = false;
      state.hasUserListError = true;
      state.userList = [];
    });
  },
});

export const {
  updateCampaign,
  deleteCampaign,
  updateCurrentCampaignFilters,
  setCurrentCampaignFiltersTypes,
  resetProfileFilters,
  resetCategoryFilter,
  resetClientsFilter,
  updateCurrentCamapignCrmFields,
  deleteCurrentCampaignCrmField,
  updateCurrentCampaign,
  updateCurrentCampaignNotificationData,
  resetCurrent,
  resetCouponClaims,
  openCampaignValidationDialog,
  closeCampaignValidationDialog,
  setCampaignValidationDialog,
  updateCurrentFullCampaign,
  updateisNewsDifferentFromCampaign,
  updateCurrentCoupon,
  updateCurrentNews,
  updateCurrentLinkStatus,
  initTemplate,
  updateSelectCampaigninfo,
  setFormCouponIsVisible,
  setFormPostcodeIsVisible,
  setAddressPostcode,
  setHasErrorFilter,
  setNotifiedUsers,
  setNotifiedUsersLoading,
  setUserList,
  resetNotificationSlice,
  setCampaignsAreLoading,
  updateWarningDialogState,
  resetWarningDialogState,
  updateAdvancedFiltersValid,
  updateCampaignTemplateDialog,
} = actions;
